import { useState, useCallback, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { useFilters } from '@/context/FiltersContext';
import { MIN_RADIUS, MAX_RADIUS } from '@/constants';

interface RadiusSliderProps {
  className?: string;
  showTicks?: boolean;
}

const TICK_VALUES = [5, 10, 25, 50];
const DEBOUNCE_MS = 300;

export function RadiusSlider({ className, showTicks = true }: RadiusSliderProps) {
  const { radius, setRadius } = useFilters();
  const [localValue, setLocalValue] = useState(radius);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local value when context value changes externally
  useEffect(() => {
    setLocalValue(radius);
  }, [radius]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);
      setLocalValue(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        setRadius(value);
      }, DEBOUNCE_MS);
    },
    [setRadius]
  );

  // Snap to tick on release
  const handleMouseUp = useCallback(() => {
    // Find closest tick value
    const closest = TICK_VALUES.reduce((prev, curr) =>
      Math.abs(curr - localValue) < Math.abs(prev - localValue) ? curr : prev
    );

    // Only snap if we're close to a tick (within 3 miles)
    if (Math.abs(closest - localValue) <= 3) {
      setLocalValue(closest);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      setRadius(closest);
    }
  }, [localValue, setRadius]);

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Calculate fill percentage for gradient
  const fillPercent = ((localValue - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS)) * 100;

  return (
    <div className={clsx('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">Search radius</label>
        <span className="text-sm font-semibold text-brand-600">{localValue} miles</span>
      </div>

      <div className="relative pt-1 pb-6">
        <input
          type="range"
          min={MIN_RADIUS}
          max={MAX_RADIUS}
          step={1}
          value={localValue}
          onChange={handleChange}
          onMouseUp={handleMouseUp}
          onTouchEnd={handleMouseUp}
          className={clsx(
            'w-full h-2 rounded-full appearance-none cursor-pointer',
            'bg-gray-200',
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:w-5',
            '[&::-webkit-slider-thumb]:h-5',
            '[&::-webkit-slider-thumb]:rounded-full',
            '[&::-webkit-slider-thumb]:bg-brand-600',
            '[&::-webkit-slider-thumb]:shadow-lg',
            '[&::-webkit-slider-thumb]:cursor-pointer',
            '[&::-webkit-slider-thumb]:border-2',
            '[&::-webkit-slider-thumb]:border-white',
            '[&::-webkit-slider-thumb]:transition-transform',
            '[&::-webkit-slider-thumb]:hover:scale-110',
            '[&::-moz-range-thumb]:w-5',
            '[&::-moz-range-thumb]:h-5',
            '[&::-moz-range-thumb]:rounded-full',
            '[&::-moz-range-thumb]:bg-brand-600',
            '[&::-moz-range-thumb]:border-2',
            '[&::-moz-range-thumb]:border-white',
            '[&::-moz-range-thumb]:cursor-pointer'
          )}
          style={{
            background: `linear-gradient(to right, #4F46E5 0%, #4F46E5 ${fillPercent}%, #E5E7EB ${fillPercent}%, #E5E7EB 100%)`,
          }}
        />

        {/* Tick marks */}
        {showTicks && (
          <div className="absolute bottom-0 left-0 right-0 flex justify-between px-[10px]">
            {TICK_VALUES.map((tick) => {
              const leftPercent = ((tick - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS)) * 100;
              const isActive = localValue >= tick;
              return (
                <button
                  key={tick}
                  type="button"
                  onClick={() => {
                    setLocalValue(tick);
                    if (debounceRef.current) {
                      clearTimeout(debounceRef.current);
                    }
                    setRadius(tick);
                  }}
                  className={clsx(
                    'absolute transform -translate-x-1/2 flex flex-col items-center group',
                    'focus:outline-none'
                  )}
                  style={{ left: `${leftPercent}%` }}
                >
                  <div
                    className={clsx(
                      'w-1.5 h-1.5 rounded-full transition-colors',
                      isActive ? 'bg-brand-600' : 'bg-gray-300',
                      'group-hover:bg-brand-400'
                    )}
                  />
                  <span
                    className={clsx(
                      'text-xs mt-1 transition-colors',
                      tick === localValue
                        ? 'text-brand-600 font-medium'
                        : 'text-gray-400 group-hover:text-gray-600'
                    )}
                  >
                    {tick}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
